import { Col, Form, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';

import { ERozsahPPN } from 'services/dokumenty/navyseni';
import { IKoducastnika, ueKoducastnika } from 'services/specialniSluzby/ueKoducastnika';
import {
    IDataZalozeniPOSTid,
    IStavOvereni,
    IZalozeniPostId,
    ueZalozeniPostId
} from 'services/specialniSluzby/ueZalozeni';

import { pocetCislic } from 'enums/regExp';
import { ETypSeznam } from 'enums/specialniSluzby/typSeznam';

import { typTransakceSelect } from 'data/codebooks/specialniSluzby/typTransakce';
import trueFalseSelect from 'data/codebooks/trueFalseSelect';

import getLanguageObject from 'utils/getLanguageObject';
import { validateICO } from 'utils/validations/icoValidation';
import { validateNID } from 'utils/validations/nidValidation';
import numberValidation from 'utils/validations/numberValidation';
import { validateRdc } from 'utils/validations/rodneCisloValidation';

const NovyRucni: React.FC = () => {
    const { specialniSluzby, language } = useContext(Context);
    const { navigateWorkaround } = useNavigateWorkaround();
    const [form] = Form.useForm();
    const [errStatus, setErrStatus] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [typPozadavku, setTypPozadavku] = useState<string>();
    const [modalResp, setModalResp] = useState<string>('');
    const [koducastnika, setKoducastnika] = useState<{ value: string; labelcz: string }[]>([]);
    const [idvlastnikaICO, setIdvlastnikaICO] = useState<boolean>(false);
    const [idvlastnikaRDC, setIdvlastnikaRDC] = useState<boolean>(false);
    const [idvlastnikaNID, setIdvlastnikaNID] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);

    useEffect(() => {
        specialniSluzby && setTypPozadavku(specialniSluzby.detail?.typ);
        setErrStatus(0);
    }, [specialniSluzby]);

    useEffect(() => {
        if (specialniSluzby?.updateDetailu) {
            form.setFieldsValue({
                idnabyvatele: specialniSluzby.pozadavek?.idnabyvatele,
                koducastnika: specialniSluzby.pozadavek?.koducastnika,
                ucet: specialniSluzby.pozadavek?.ucet,
                typtransakce: specialniSluzby.pozadavek?.typtransakce,
                pocetcp: specialniSluzby.pozadavek?.pocetcp,
                cislozastavnismlouvy: specialniSluzby.pozadavek?.cislozastavnismlouvy,
                idvlastnika: specialniSluzby.pozadavek?.idvlastnika,
                prijmeni: specialniSluzby.pozadavek?.prijmeni,
                jmeno: specialniSluzby.pozadavek?.jmeno
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [specialniSluzby?.updateDetailu]);

    useEffect(() => {
        setLoading(true);
        ueKoducastnika()
            .then((resp: IKoducastnika) => {
                const tmpSelectArr: { value: string; labelcz: string }[] = [];
                resp.kodyucastnika.forEach(item => {
                    (specialniSluzby?.detail?.typ !== 'UPIS' ? item.kod !== '100' : true) &&
                        tmpSelectArr.push({
                            value: item.kod,
                            labelcz: item.kod + ' - ' + item.nazev
                        });
                });
                setKoducastnika(tmpSelectArr);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sendForm = () => {
        const vals = form.getFieldsValue();
        setLoading(true);
        specialniSluzby?.detail?.id &&
            ueZalozeniPostId(
                specialniSluzby.detail.id,
                Object.fromEntries(
                    Object.entries({
                        ...vals,
                        ...(vals.ppn && {
                            ppn: vals.ppn === 'true'
                        }),
                        idpolozky: specialniSluzby?.updateDetailu ? specialniSluzby.pozadavek?.idpolozky : null
                    }).filter(
                        ([, e]) => e !== null && e !== undefined && e !== '' && !(typeof e === 'number' && isNaN(e))
                    )
                ) as unknown as IDataZalozeniPOSTid
            )
                .then((resp: IZalozeniPostId) => {
                    if (resp.stavovereni === IStavOvereni.NENALEZENO && resp.stav === 'ODMITNUTO') setModalResp('UCET');
                    if (resp.stavovereni === IStavOvereni.VLASTNIK && resp.stav === 'ODMITNUTO')
                        setModalResp('VLASTNIK');
                    else if (resp.sankce) setModalResp('SANKCE');
                    else if (resp.stav === 'OK') navigateWorkaround(-1);
                    else setModalResp(resp.stav);
                })
                .catch((err: any) => console.log('Zalozeni POSTid err', err))
                .finally(() => setLoading(false));
    };

    const renderModal = () => {
        return (
            <Modal
                title={<TranslateText>SS_NOVY_RUCNI_MODAL_ZAPIS</TranslateText>}
                open={modalResp !== ''}
                onCancel={() => {
                    modalResp === 'SANKCE' && navigateWorkaround(-1);
                    setModalResp('');
                }}
                footer={[
                    <Row>
                        <ButtonComponent
                            buttontext="ZAVRIT"
                            onClick={() => {
                                modalResp === 'SANKCE' && navigateWorkaround(-1);
                                setModalResp('');
                            }}
                            id="zavritModalBtn"
                        />
                    </Row>
                ]}
            >
                <Row>
                    <Paragraph
                        text={
                            modalResp === 'TIMEOUT'
                                ? 'SS_SLUZBA_ERROR_NESKORO'
                                : 'SS_NOVY_RUCNI_MODAL_RESP_' + modalResp
                        }
                    />
                </Row>
            </Modal>
        );
    };

    return (
        <LoadingWrapper loading={loading}>
            <Title
                level={4}
                text={`${getLanguageObject(language)['SS_NOVY_POZADAVEK_NA_' + specialniSluzby?.detail?.typ]} - ${
                    getLanguageObject(language).ISIN
                }: ${specialniSluzby?.detail?.isin}, ${getLanguageObject(language).DATUM}: ${dayjs(
                    specialniSluzby?.detail?.datum
                ).format('DD.MM.YYYY')}`}
            />
            {errStatus ? (
                <Row>
                    <Col span={16}>
                        {errStatus === 404 ? (
                            <Paragraph text="RESPONSE_ERROR_404" />
                        ) : errStatus ? (
                            <Paragraph text="SLUZBA_NEDOSTUPNA" />
                        ) : null}
                        <Row className="button-row">
                            <ButtonComponent
                                buttontext="ZPET"
                                onClick={() => {
                                    setErrStatus(0);
                                }}
                                type="default"
                                id="zpetErrBtn"
                            />
                        </Row>
                    </Col>
                </Row>
            ) : (
                <>
                    <Row>
                        <FormWrapper onFinish={sendForm} layoutPosition="vertical" outForm={form}>
                            <SelectComponent
                                isFormItem={true}
                                data={koducastnika || []}
                                name="koducastnika"
                                defaultValue={
                                    specialniSluzby?.updateDetailu ? specialniSluzby.pozadavek?.koducastnika : undefined
                                }
                                labelParagraph={'KOD_UCASTNIKA'}
                            />
                            <InputComponent
                                name="ucet"
                                label="UCET"
                                initialValue={
                                    specialniSluzby?.updateDetailu ? specialniSluzby.pozadavek?.ucet : undefined
                                }
                                maxLength={12}
                                rules={[
                                    () => ({
                                        validator(_, value: string) {
                                            if (!value) return Promise.resolve();
                                            if (/^(100|808|909)\d{9}$/.test(value)) return Promise.resolve();
                                            return Promise.reject(
                                                <TranslateText>SS_NOVY_RUCNI_VALIDACE_UCET</TranslateText>
                                            );
                                        }
                                    })
                                ]}
                            />
                            <InputComponent
                                name="idnabyvatele"
                                label="ID_NABYVATELE"
                                initialValue={
                                    specialniSluzby?.updateDetailu ? specialniSluzby.pozadavek?.idnabyvatele : undefined
                                }
                                maxLength={10}
                                rules={[
                                    {
                                        validator(_rule, value: string) {
                                            if (!value) return Promise.resolve();
                                            if (pocetCislic(8, 10).test(value)) return Promise.resolve();
                                            return Promise.reject(
                                                <TranslateText>SS_NOVY_RUCNI_VALIDACE_IDNABYVATELE_CISLA</TranslateText>
                                            );
                                        }
                                    }
                                ]}
                            />
                            {(typPozadavku === 'SICAV' ||
                                typPozadavku === 'SICAVSNIZENI' ||
                                typPozadavku === 'SICAVNAVYSENI') && (
                                <SelectComponent
                                    isFormItem={true}
                                    data={typTransakceSelect}
                                    disabled={
                                        specialniSluzby?.seznamTyp !== ETypSeznam.SMAZANY &&
                                        specialniSluzby?.detail?.polozky?.length &&
                                        specialniSluzby.detail.polozky[0].typtransakce
                                            ? true
                                            : false
                                    }
                                    name="typtransakce"
                                    defaultValue={
                                        specialniSluzby?.seznamTyp !== ETypSeznam.SMAZANY &&
                                        specialniSluzby?.detail?.polozky?.length &&
                                        specialniSluzby.detail.polozky[0].typtransakce
                                            ? specialniSluzby.detail.polozky[0].typtransakce
                                            : specialniSluzby?.updateDetailu
                                              ? specialniSluzby.pozadavek?.typtransakce
                                              : undefined
                                    }
                                    labelParagraph={'SS_DETAIL_FILTR_TYP'}
                                />
                            )}
                            <InputNumberComponent
                                name="pocetcp"
                                label="POCET_CP"
                                initialValue={
                                    specialniSluzby?.updateDetailu ? specialniSluzby.pozadavek?.pocetcp : undefined
                                }
                                maxLength={12}
                                precision={0}
                                rules={numberValidation(true)}
                            />

                            {specialniSluzby?.detail?.ppn && (
                                <SelectComponent
                                    isFormItem={true}
                                    data={trueFalseSelect}
                                    name="ppn"
                                    defaultValue={
                                        specialniSluzby.detail.rozsahppn === ERozsahPPN.VSECHNY
                                            ? 'true'
                                            : specialniSluzby?.updateDetailu &&
                                                typeof specialniSluzby?.pozadavek?.ppn === 'boolean'
                                              ? String(specialniSluzby.pozadavek.ppn)
                                              : undefined
                                    }
                                    labelParagraph={'PPN_SELECT'}
                                    disabled={
                                        (!!specialniSluzby?.pozadavek?.cislozastavnismlouvy &&
                                            specialniSluzby?.pozadavek?.ppn === true) ||
                                        specialniSluzby.detail.rozsahppn === ERozsahPPN.VSECHNY
                                    }
                                    onChange={val => {
                                        val === 'true' &&
                                            form.setFieldsValue({
                                                cislozastavnismlouvy: undefined
                                            });
                                        rerender(pv => !pv);
                                    }}
                                />
                            )}
                            {typPozadavku !== 'SICAV' &&
                                typPozadavku !== 'SNIZENI' &&
                                typPozadavku !== 'SICAVSNIZENI' &&
                                typPozadavku !== 'SICAVNAVYSENI' &&
                                (specialniSluzby?.detail?.ppn ? form.getFieldValue('ppn') === 'false' : true) && (
                                    <InputComponent
                                        name="cislozastavnismlouvy"
                                        label="CISLO_ZASTAVNI_SMLOUVY"
                                        initialValue={
                                            specialniSluzby?.updateDetailu
                                                ? specialniSluzby.pozadavek?.cislozastavnismlouvy
                                                : undefined
                                        }
                                        maxLength={12}
                                        rules={[
                                            () => ({
                                                validator(_, value: string) {
                                                    if (!value) return Promise.resolve();
                                                    if (/^6\d{11}$/.test(value)) return Promise.resolve();
                                                    return Promise.reject(
                                                        <TranslateText>SS_NOVY_RUCNI_VALIDACE_ZASTAVNI</TranslateText>
                                                    );
                                                }
                                            })
                                        ]}
                                        notRequired={true}
                                        onChange={() => rerender(pv => !pv)}
                                        onBlur={() =>
                                            form.getFieldValue('cislozastavnismlouvy') &&
                                            form.setFieldsValue({
                                                ppn: 'false'
                                            })
                                        }
                                        disabled={
                                            specialniSluzby?.detail?.rozsahppn === ERozsahPPN.VSECHNY ||
                                            (!!specialniSluzby?.pozadavek?.cislozastavnismlouvy &&
                                                specialniSluzby?.pozadavek?.ppn === true)
                                        }
                                    />
                                )}
                            <Paragraph text={'SS_NOVY_RUCNI_UVEDTE'} />
                            <InputComponent
                                name="idvlastnika"
                                label="SS_NOVY_RUCNI_ID_C_VLASTNIKA"
                                initialValue={
                                    specialniSluzby?.updateDetailu ? specialniSluzby.pozadavek?.idvlastnika : undefined
                                }
                                maxLength={10}
                                notRequired={true}
                                rules={[
                                    () => ({
                                        validator(_, value: string) {
                                            if (!value) return Promise.resolve();
                                            if (
                                                validateICO(value) === 0 ||
                                                validateRdc(value) === 0 ||
                                                validateNID(value) === 0
                                            )
                                                return Promise.resolve();
                                            return Promise.reject(
                                                <TranslateText>UPOZORNENI_NEVALIDNIRC</TranslateText>
                                            );
                                        }
                                    })
                                ]}
                                onBlur={e => {
                                    const value = e.target.value;
                                    setIdvlastnikaICO(validateICO(value) === 0 ? true : false);
                                    setIdvlastnikaRDC(validateRdc(value) === 0 ? true : false);
                                    setIdvlastnikaNID(validateNID(value) === 0 ? true : false);
                                    form.setFields([
                                        {
                                            name: 'prijmeni',
                                            errors: []
                                        },
                                        {
                                            name: 'jmeno',
                                            errors: []
                                        }
                                    ]);
                                }}
                            />
                            <InputComponent
                                name="prijmeni"
                                label="SS_NOVY_RUCNI_PRIJMENI"
                                initialValue={
                                    specialniSluzby?.updateDetailu ? specialniSluzby.pozadavek?.prijmeni : undefined
                                }
                                maxLength={80}
                                notRequired={!(idvlastnikaICO || idvlastnikaRDC || idvlastnikaNID)}
                                rules={[
                                    {
                                        max: 80,
                                        message: <TranslateText>NEVALIDNI_POCET_ZNAKU</TranslateText>
                                    }
                                ]}
                            />
                            <InputComponent
                                name="jmeno"
                                label="SS_NOVY_RUCNI_JMENO"
                                initialValue={
                                    specialniSluzby?.updateDetailu ? specialniSluzby.pozadavek?.jmeno : undefined
                                }
                                maxLength={80}
                                notRequired={!idvlastnikaRDC}
                                rules={[
                                    {
                                        max: 80,
                                        message: <TranslateText>NEVALIDNI_POCET_ZNAKU</TranslateText>
                                    }
                                ]}
                            />
                            <Row className="button-row">
                                <ButtonComponent
                                    buttontext="ZPET"
                                    type="default"
                                    onClick={() => {
                                        navigateWorkaround(-1);
                                    }}
                                    id="zpetBtn"
                                />
                                <ButtonComponent buttontext="ZAPSAT" onClick={() => form.submit()} id="zapsatBtn" />
                            </Row>
                        </FormWrapper>
                    </Row>
                    {renderModal()}
                </>
            )}
        </LoadingWrapper>
    );
};

export default NovyRucni;
